import { Component } from '@angular/core';

import type { FaqItem, PictureCredit } from '@shared/components';
import { CtaPanel, Faq } from '@shared/components';

import { CostOrComplexity } from './cost-or-complexity/cost-or-complexity';
import { EvrnExports } from './evrn-exports/evrn-exports';
import { EvrnMethod } from './evrn-method/evrn-method';
import { EvrnToVideo } from './evrn-to-video/evrn-to-video';
import { Hero } from './hero/hero';
import { PrevisAndProduction } from './previs-and-production/previs-and-production';

@Component({
  selector: 'app-home',
  imports: [
    Hero,
    EvrnToVideo,
    EvrnExports,
    PrevisAndProduction,
    CostOrComplexity,
    Faq,
    CtaPanel,
    EvrnMethod,
  ],
  templateUrl: './home.html',
})
export class Home {
  readonly pictureCredits: PictureCredit[] = [
    { name: 'Vinod Padmanabhan Vijay Kumar', work: 'dragon and carpenter' },
    { name: 'Michael Jones', work: 'old man with fireplace' },
    { name: 'Brandon Vietti', work: 'robot with woman' },
    {
      name: 'Anima Studios',
      work: 'galactic elder (as part of The Great Galactic War original IP)',
    },
  ];
  readonly faqs: FaqItem[] = [
    {
      question: 'What is EVRN for?',
      answer:
        'EVRN is for filmmakers, storytellers, directors, cinematographers, and technical directors who want deterministic control over generative output. Common uses include previsualization, virtual production, shot design, location scouting, and building 3D reference for generative video models. EVRN works best with clear single-perspective images where you want to isolate and direct specific hero elements e.g. characters, props, and set pieces.',
    },
    {
      question: 'How is EVRN different from other generative video platforms?',
      answer:
        "EVRN isn't a generative video platform. It's the control layer that sits between your image and any generative video model. Most generative video platforms produce video from a prompt or a flat image, generating 2D output that's difficult to direct with precision. EVRN turns your image into a fully 3D scene with individual assets you can move, light, and frame — then hands that scene off to your generative model as reference, or exports it directly to your 3D pipeline.",
    },
    {
      question:
        'Is EVRN primarily a prompt-based tool, or does it rely more on image inputs and scene composition? How much prompt engineering is involved in achieving consistent results?',
      answer:
        "None. EVRN isn't a prompt-based tool. You upload a flat image — any image — identify the hero assets you want in 3D, and EVRN builds a directable 3D scene in around 20 minutes. Control comes from your image, your identification of hero assets, and your direction of the camera.",
    },
    {
      question: 'How does EVRN give me control?',
      answer:
        'Through virtual cinematography. Inside the EVRN 3D editor, you work with real cinematic controls — cameras, lenses, focal lengths, apertures, and framing — the same instruments a director of photography uses on set. You position cameras in your scene, choose focal length and animate the camera along a path. What you see in the viewer is what your generative video model will receive as reference: a precisely framed, professionally composed shot.',
    },
    {
      question: 'Can I control the camera?',
      answer:
        'Yes. In the EVRN 3D editor, you position the camera, set framing, and animate camera paths through your scene, all in your browser. Export the result as a rendered video or key frame stills for your generative video pipeline.',
    },
    {
      question: "What are EVRN's ideal use cases?",
      answer:
        "EVRN is built for anyone working with generative AI or 3D who needs speed and directability. We're seeing strong results across generative video production (as a layout reference for frame-accurate renders), previsualization (before full previs or set construction), 3D asset planning (blocking composition and camera before opening a DCC), and animation (particularly short-form content teams producing daily streaming output).",
    },
    {
      // NOTE: the answer below is the one paired with this question in Figma, but it answers an
      // API/pipeline question rather than the experience-level question. Needs copy from design.
      question:
        'What level of experience do you recommend before using EVRN? Is it suitable for beginners, or should users already have experience with filmmaking, cinematography, virtual production, or 3D workflows?',
      answer:
        'Not yet. API access and DCC bridges are coming later in 2026, which will let you connect EVRN to automation tools and your own pipeline.',
    },
    {
      question: 'Which devices and browsers does EVRN support?',
      answer:
        'EVRN runs in the browser. It works on any modern computer with a stable internet connection. We recommend Chrome for the best experience. Tablet support is coming.',
    },
    {
      question: 'Are your scenes frozen, like Gaussian Splats?',
      answer:
        'No. EVRN creates individual assets placed inside an environment you can manipulate. In your 2D image, you identify which assets you want EVRN to turn into 3D and the rest of the environment becomes one static asset.',
    },
    {
      question: 'How long does it take to generate a 3D world?',
      answer: 'Around 20 minutes.',
    },
    {
      question: 'Which image formats does EVRN accept?',
      answer: 'PNG, JPG, and WebP.',
    },
    {
      question: 'Can I upload several pictures for EVRN to reconstruct the 3D scene?',
      answer:
        'Not at the moment. EVRN processes one image at a time. If you want your scene to include several characters or assets, composite them into a single image before uploading.',
    },
    {
      question: 'Can I upload my own 3D assets to EVRN?',
      answer: "Not yet. It's on our roadmap.",
    },
    {
      question: 'Do you have generative video models embedded in the platform?',
      answer:
        "Not at launch but yes it's on our roadmap. For now, EVRN produces the layout reference: a directed 3D scene with your camera framing, exported as video or images. You bring that reference into your generative video platform of choice for the final output.",
    },
    {
      question: 'Does EVRN accept video input?',
      answer: 'No. EVRN only accepts 2D picture inputs.',
    },
    {
      question: 'What file formats can I export?',
      answer:
        'At launch: rendered videos (MP4) and images (PNG). You direct the camera path in the EVRN 3D editor, and export the result to bring into your generative video pipeline or editorial. Full 3D scene export (GLB, USD, OBJ) with individual hero assets as separate meshes is coming after launch.',
    },
    {
      question: 'What is a mesh?',
      answer:
        'A mesh is the 3D shape of an object. Its surface, defined by connected points and edges. When EVRN turns your image into a scene, each asset becomes its own mesh you can move, light, and direct.',
    },
    {
      question: 'How complex are the 3D scenes EVRN creates?',
      answer:
        'Each hero asset EVRN identifies from your image is a mesh of around 30,000 polygons, optimized for real-time performance in the browser viewer while retaining enough detail for downstream use. The background environment is a single static mesh separate from the hero assets. Scenes can contain between three to 20+ hero assets, depending on what you identify in the image.',
    },
    {
      question: 'Is there a plugin for Blender, Maya, or Unreal?',
      answer:
        "Not yet. In the coming months, we'll release a lightweight bridge: a one-click open in Blender or open in Unreal that hands your EVRN scene off to your DCC as GLB, USD, or OBJ.",
    },
    {
      question: 'Do you offer an API?',
      answer: 'Not at the moment. API access is on our roadmap.',
    },
    {
      question: 'Can I request a feature or report a bug?',
      answer:
        'Yes, please. Join our <a href="https://discord.com/invite/eBGr5Vzsyk" class="faq__inner-link" target="_blank" rel="noopener noreferrer">Discord</a> community and share your feedback there.',
    },
    {
      question: "How do I share my world with someone who doesn't have an EVRN account?",
      answer:
        'Following months after launch, we will allow downloading of 3D assets as OBJ, USD and GLB. You will be able to share them that way. At the moment, we do not allow URL-based sharing.',
    },
    {
      question: 'Can EVRN accommodate our enterprise legal, security, and compliance requirements?',
      answer:
        'Yes. EVRN offers enterprise agreements with custom terms, including a Master Services Agreement, Data Processing Agreement, and clear IP ownership provisions. We work with major studios and are prepared to review your specific requirements around security, data handling, and indemnification. Contact <a href="mailto:enterprise@evrn.world" class="faq__inner-link" target="_blank" rel="noopener noreferrer">enterprise&#64;evrn.world</a> we look forward to working with you.',
    },
  ];
}
